import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { invalidateReplyQueueMembership } from '@/lib/crm/reply-queue-cache';

// R9 B4.6: source filter values
export type DraftSourceFilter = 'all' | 'product' | 'other';

export type DraftStatus =
  | 'all'
  | 'pending_approval'
  | 'approved'
  | 'sent'
  | 'rejected'
  | 'failed'
  | 'awaiting_draft';
export type SenderFilter = string | 'all' | 'unassigned';

export interface DraftVersion {
  text: string;
  methodology: string;
  confidence: number;
  approach?: string;
  generated_at: string;
  instruction?: string;
}

export interface ConstraintResult {
  id: string;
  rule: string;
  passed: boolean;
  detail?: string;
}

export interface ConstraintValidation {
  passed: boolean;
  score: number;
  total: number;
  constraints: ConstraintResult[];
}

export interface DraftListItem {
  id: string;
  organization_id: string;
  user_id: string | null;
  lead_name: string;
  lead_title: string | null;
  lead_company: string | null;
  lead_linkedin_url: string | null;
  incoming_message: string;
  incoming_at: string;
  draft_response: string;
  edited_response: string | null;
  methodology_used: string;
  confidence: number;
  detected_language: string | null;
  analysis_report: Record<string, unknown> | null;
  strategy: Record<string, unknown> | null;
  alternatives: Array<Record<string, unknown>> | null;
  decision_trace: Record<string, unknown> | null;
  selected_alternative: number | null;
  human_edit_diff: string | null;
  status: 'pending_approval' | 'approved' | 'sent' | 'rejected' | 'failed';
  created_at: string;
  heyreach_campaign_id: string;
  heyreach_lead_id: string;
  heyreach_sender_id: string | null;
  sender_id: string | null;
  sender: {
    id: string;
    display_name: string;
  } | null;
  // Smart Regeneration fields
  draft_versions: DraftVersion[] | null;
  regenerated_at: string | null;
  regeneration_count: number | null;
  constraint_validation: ConstraintValidation | null;
  // R9 B4.6: source allowlist (absent pre-migration-029)
  source?: string | null;
}

export interface DraftFilters {
  status: DraftStatus;
  senderId: SenderFilter;
  search: string;
  page: number;
  pageSize: number;
  // R9 B4.6: source allowlist filter (default 'all')
  source: DraftSourceFilter;
}

export interface DraftQueryResult {
  drafts: DraftListItem[];
  total: number;
  hasMore: boolean;
  page: number;
  pageSize: number;
}

const DEFAULT_FILTERS: DraftFilters = {
  status: 'pending_approval',
  senderId: 'all',
  search: '',
  page: 1,
  pageSize: 20,
  source: 'all',
};

const DRAFT_COLUMNS = `
  organization_id,
  user_id,
  id,
  lead_name,
  lead_title,
  lead_company,
  lead_linkedin_url,
  incoming_message,
  incoming_at,
  draft_response,
  edited_response,
  methodology_used,
  confidence,
  detected_language,
  analysis_report,
  strategy,
  alternatives,
  decision_trace,
  selected_alternative,
  human_edit_diff,
  status,
  created_at,
  heyreach_campaign_id,
  heyreach_lead_id,
  heyreach_sender_id,
  sender_id,
  sender:os_heyreach_senders(id, display_name),
  draft_versions,
  regenerated_at,
  regeneration_count,
  constraint_validation,
  source
` as const;

// R9 B4.6: DRAFT_COLUMNS without source — used as fallback if column is absent
// (pre-migration-029). This prevents a 42703 from breaking the draft list.
const DRAFT_COLUMNS_LEGACY = `
  organization_id,
  user_id,
  id,
  lead_name,
  lead_title,
  lead_company,
  lead_linkedin_url,
  incoming_message,
  incoming_at,
  draft_response,
  edited_response,
  methodology_used,
  confidence,
  detected_language,
  analysis_report,
  strategy,
  alternatives,
  decision_trace,
  selected_alternative,
  human_edit_diff,
  status,
  created_at,
  heyreach_campaign_id,
  heyreach_lead_id,
  heyreach_sender_id,
  sender_id,
  sender:os_heyreach_senders(id, display_name),
  draft_versions,
  regenerated_at,
  regeneration_count,
  constraint_validation
` as const;

/**
 * Detect a missing-column error from PostgREST/Postgres returned via
 * Supabase's data layer. The error object shape from @supabase/supabase-js
 * wraps Postgres/PostgREST codes in error.code and error.message.
 */
const isMissingColumnError = (err: { code?: string; message?: string } | null): boolean => {
  if (!err) return false;
  if (err.code === '42703') return true;
  // PGRST204 intentionally omitted: it is a write-side schema-cache code and
  // cannot arise on this SELECT-only path; Postgres returns 42703 for unknown columns.
  if (err.message && /column\s+.*source.*\s+does not exist/i.test(err.message)) return true;
  return false;
};

export const useLinkedInDraftFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo((): DraftFilters => {
    const statusParam = searchParams.get('status') as DraftStatus | null;
    const senderParam = searchParams.get('sender');
    const searchParam = searchParams.get('q') || DEFAULT_FILTERS.search;
    const pageParam = Number.parseInt(searchParams.get('page') || '1', 10);
    const page = Number.isNaN(pageParam) || pageParam < 1 ? 1 : pageParam;
    const allowedStatuses: DraftStatus[] = [
      'pending_approval',
      'approved',
      'sent',
      'rejected',
      'failed',
      'all',
    ];
    const status = statusParam && allowedStatuses.includes(statusParam)
      ? statusParam
      : DEFAULT_FILTERS.status;

    const sourceParam = searchParams.get('source') as DraftSourceFilter | null;
    const allowedSources: DraftSourceFilter[] = ['all', 'product', 'other'];
    const source = sourceParam && allowedSources.includes(sourceParam)
      ? sourceParam
      : DEFAULT_FILTERS.source;

    return {
      status,
      senderId: senderParam || DEFAULT_FILTERS.senderId,
      search: searchParam,
      page,
      pageSize: DEFAULT_FILTERS.pageSize,
      source,
    };
  }, [searchParams]);

  const setFilters = useCallback(
    (updates: Partial<DraftFilters>) => {
      const newParams = new URLSearchParams(searchParams);
      if (updates.status !== undefined) {
        newParams.set('status', updates.status);
      }
      if (updates.senderId !== undefined) {
        newParams.set('sender', updates.senderId);
      }
      if (updates.search !== undefined) {
        if (updates.search) {
          newParams.set('q', updates.search);
        } else {
          newParams.delete('q');
        }
      }
      if (updates.page !== undefined) {
        newParams.set('page', String(updates.page));
      }
      if (updates.source !== undefined) {
        if (updates.source !== 'all') {
          newParams.set('source', updates.source);
        } else {
          newParams.delete('source');
        }
      }
      if (
        updates.status !== undefined ||
        updates.senderId !== undefined ||
        updates.search !== undefined ||
        updates.source !== undefined
      ) {
        newParams.set('page', '1');
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  return { filters, setFilters };
};

export const useLinkedInDrafts = (filters: DraftFilters) => {
  const queryClient = useQueryClient();

  const query = useQuery<DraftQueryResult>({
    queryKey: ['linkedin-drafts', filters],
    queryFn: async () => {
      const { page, pageSize, status, senderId, source } = filters;
      const offset = (page - 1) * pageSize;
      const search = filters.search.trim();

      /**
       * R9 B4.6: column-tolerant source filter.
       *
       * First attempt: query with `source` column (DRAFT_COLUMNS) and apply
       * source filter when active. On missing-column error (42703 / PGRST204),
       * fall back to DRAFT_COLUMNS_LEGACY (without source) and skip filter.
       * This ensures the draft list never breaks pre-migration-029.
       */

      // Build primary query with source column
      let primaryQuery = supabase
        .from('os_linkedin_drafts')
        .select(DRAFT_COLUMNS, { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(offset, offset + pageSize - 1);

      if (status !== 'all') {
        primaryQuery = primaryQuery.eq('status', status);
      }
      if (senderId === 'unassigned') {
        primaryQuery = primaryQuery.is('sender_id', null);
      } else if (senderId !== 'all') {
        primaryQuery = primaryQuery.eq('sender_id', senderId);
      }
      if (search.length > 0) {
        const safeSearch = search.replace(/,/g, ' ');
        primaryQuery = primaryQuery.or(
          `lead_name.ilike.%${safeSearch}%,lead_company.ilike.%${safeSearch}%,incoming_message.ilike.%${safeSearch}%`
        );
      }
      if (source === 'product') {
        primaryQuery = primaryQuery.eq('source', 'product');
      } else if (source === 'other') {
        primaryQuery = primaryQuery.neq('source', 'product');
      }

      let { data, error, count } = await primaryQuery;

      // Column-tolerance fallback: retry without source on missing-column error
      if (error && isMissingColumnError(error)) {
        let legacyQuery = supabase
          .from('os_linkedin_drafts')
          .select(DRAFT_COLUMNS_LEGACY, { count: 'exact' })
          .order('created_at', { ascending: false })
          .range(offset, offset + pageSize - 1);

        if (status !== 'all') {
          legacyQuery = legacyQuery.eq('status', status);
        }
        if (senderId === 'unassigned') {
          legacyQuery = legacyQuery.is('sender_id', null);
        } else if (senderId !== 'all') {
          legacyQuery = legacyQuery.eq('sender_id', senderId);
        }
        if (search.length > 0) {
          const safeSearch = search.replace(/,/g, ' ');
          legacyQuery = legacyQuery.or(
            `lead_name.ilike.%${safeSearch}%,lead_company.ilike.%${safeSearch}%,incoming_message.ilike.%${safeSearch}%`
          );
        }
        // Source filter intentionally skipped in legacy fallback (column absent)

        const fallback = await legacyQuery;
        // approved: legacy data lacks `source` column; cast compatible since DraftListItem.source is optional
        data = fallback.data as typeof data;
        error = fallback.error;
        count = fallback.count;
      }

      if (error) throw error;

      const drafts = (data || []).map((draft) => ({
        ...draft,
        sender:
          Array.isArray(draft.sender) && draft.sender.length > 0 ? draft.sender[0] : null,
      }));

      return {
        drafts: drafts as DraftListItem[],
        total: count || 0,
        hasMore: (count || 0) > offset + pageSize,
        page,
        pageSize,
      };
    },
    staleTime: 10000,
    placeholderData: (previousData) => previousData,
  });

  const { user, isAdmin, organizationId } = useAuth();

  useEffect(() => {
    if (!user?.id || !organizationId) return;

    let pollingInterval: ReturnType<typeof setInterval> | null = null;

    // User-scoped realtime: members only get notified of their own drafts.
    // Admins/owners get notified of all org drafts.
    const realtimeFilter = isAdmin
      ? `organization_id=eq.${organizationId}`
      : `user_id=eq.${user.id}`;

    const channel = supabase
      .channel(`drafts-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'os_linkedin_drafts',
          filter: realtimeFilter,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['linkedin-drafts'] });
          queryClient.invalidateQueries({ queryKey: ['linkedin-stats'] });
        }
      )
      .subscribe((status) => {
        if ((status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') && !pollingInterval) {
          pollingInterval = setInterval(() => {
            queryClient.invalidateQueries({ queryKey: ['linkedin-drafts'] });
          }, 30000);
        }
      });

    return () => {
      supabase.removeChannel(channel);
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [queryClient, user?.id, isAdmin, organizationId]);

  return query;
};

export const useLinkedInDraftCounts = () => {
  return useQuery({
    queryKey: ['linkedin-draft-counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('os_linkedin_drafts')
        .select('status');

      if (error) throw error;

      const counts = {
        pending_approval: 0,
        approved: 0,
        sent: 0,
        rejected: 0,
        failed: 0,
        all: 0,
      };

      (data as Array<{ status: string }> | null || []).forEach((draft) => {
        const status = draft.status as keyof typeof counts;
        if (status in counts) {
          counts[status] += 1;
        }
        counts.all += 1;
      });

      return counts;
    },
    staleTime: 30000,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// Regeneration Mutation
// ─────────────────────────────────────────────────────────────────────────────

export interface RegenerateResult {
  draftId: string;
  success: boolean;
  error?: string;
  oldMethodology?: string;
  newMethodology?: string;
  constraintScore?: number;
  /**
   * The reply gate declined to regenerate: the inbound was an explicit refusal or a removal request,
   * so no model call was made. `success` stays true — refusing is the endpoint working. Callers MUST
   * branch on this before reporting a regeneration, or they announce a new draft that does not exist.
   */
  blocked?: boolean;
  rule?: string;
  reason?: string;
  /**
   * Defer only. `false` means the 45-day follow-up date never reached the thread row, so the
   * conversation is closed with nothing to bring it back — the operator is warned instead of
   * reassured. Absent for closes and for endpoints that predate the field.
   */
  followUpScheduled?: boolean;
}

export interface RegenerateResponse {
  success: boolean;
  regenerated: number;
  failed: number;
  total: number;
  results: RegenerateResult[];
}

export interface RegenerateDraftVariables {
  draftIds: string[];
  customInstructions?: string;
  /** Deliberate operator action; ordinary regeneration must leave this absent. */
  overrideReplyGate?: boolean;
}

export const useRegenerateDraft = (): ReturnType<
  typeof useMutation<
    RegenerateResponse,
    Error,
    RegenerateDraftVariables
  >
> => {
  const queryClient = useQueryClient();

  return useMutation<RegenerateResponse, Error, RegenerateDraftVariables>({
    mutationFn: async ({ draftIds, customInstructions, overrideReplyGate }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch('/api/os/linkedin/regenerate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ draftIds, customInstructions, overrideReplyGate }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = (errorData as Record<string, unknown>)?.error
          ? String(((errorData as Record<string, unknown>).error as Record<string, unknown>)?.message || 'Regeneration failed')
          : `Regeneration failed (${response.status})`;
        throw new Error(message);
      }

      return response.json() as Promise<RegenerateResponse>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['linkedin-drafts'] });
      queryClient.invalidateQueries({ queryKey: ['linkedin-stats'] });
      queryClient.invalidateQueries({ queryKey: ['linkedin-draft-counts'] });
      queryClient.invalidateQueries({ queryKey: ['thread-messages'] });
      queryClient.invalidateQueries({ queryKey: ['conversation-threads'] });
      queryClient.invalidateQueries({ queryKey: ['thread-counts'] });
      queryClient.invalidateQueries({ queryKey: ['lead-dossier'] });
      queryClient.invalidateQueries({ queryKey: ['stale-drafts'] });
    },
    // A rejected draft enters LIVE_DRAFT_STATUSES when regeneration succeeds.
    // Settle-time invalidation also reconciles an ambiguous/partial server result.
    onSettled: () => invalidateReplyQueueMembership(queryClient),
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// On-Demand Draft Generation Hook
// ─────────────────────────────────────────────────────────────────────────────

export interface GenerateDraftResponse {
  success: boolean;
  draftId: string;
  methodology: string;
  confidence: number;
  constraintScore: number;
  error?: string;
  /** See RegenerateResult.blocked — the reply gate declined, and no model call was made. */
  blocked?: boolean;
  rule?: string;
  reason?: string;
  /** Defer only: false means the follow-up date never reached the database. */
  followUpScheduled?: boolean;
  /** Present when part of the decision failed to persist; the operator must not be told "recorded". */
  bookkeepingErrors?: string[];
}

export interface GenerateDraftVariables {
  draftId: string;
  customInstructions?: string;
  /** Deliberate operator action; ordinary generation must leave this absent. */
  overrideReplyGate?: boolean;
}

export const useGenerateDraft = (): ReturnType<
  typeof useMutation<GenerateDraftResponse, Error, GenerateDraftVariables>
> => {
  const queryClient = useQueryClient();

  return useMutation<GenerateDraftResponse, Error, GenerateDraftVariables>({
    mutationFn: async ({ draftId, customInstructions, overrideReplyGate }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch('/api/os/linkedin/generate-draft', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ draftId, customInstructions, overrideReplyGate }),
      });

      if (!response.ok) {
        if (response.status === 409) {
          throw new Error('Draft is already being generated or was already generated');
        }
        const errorData = await response.json().catch(() => ({}));
        const message = (errorData as Record<string, unknown>)?.message
          ? String((errorData as Record<string, unknown>).message)
          : `Draft generation failed (${response.status})`;
        throw new Error(message);
      }

      return response.json() as Promise<GenerateDraftResponse>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['thread-messages'] });
      queryClient.invalidateQueries({ queryKey: ['conversation-threads'] });
      queryClient.invalidateQueries({ queryKey: ['thread-counts'] });
      queryClient.invalidateQueries({ queryKey: ['linkedin-drafts'] });
      // F1: the Lead Dossier can also trigger this mutation directly (a
      // null-thread awaiting_draft has no Inbox thread view to invalidate
      // its way to) — without this, its CTA would stay stuck offering
      // "Generate Draft" after generation already succeeded.
      queryClient.invalidateQueries({ queryKey: ['lead-dossier'] });
      queryClient.invalidateQueries({ queryKey: ['stale-drafts'] });
    },
    onSettled: () => invalidateReplyQueueMembership(queryClient),
  });
};
